/**
 * Render the ExecutiveBriefing to plain Markdown for clipboard / LINE /
 * email share. Pure function — no DOM, no side effects — so the test
 * suite can assert the output without mocking.
 *
 * What the Mayor actually wants to share with staff:
 *   - The current dam/drought/aqi situation
 *   - Open incidents count
 *   - Active alerts (if any)
 *   - Top initiatives with status
 *
 * What we deliberately omit:
 *   - Internal source strings ("thaiwater.rid", "data.go.th")
 *   - Timestamp micro-detail (HH:MM:SS) — HH:MM is enough for sharing
 *   - Anything the brief itself already shows the Mayor as
 *     "MODELLED" / "static" (reservoir-comparison numbers)
 *
 * The output is in Thai canonical with English in parens where it adds
 * clarity, so staff who don't read Thai can still skim it.
 */

import type { ExecutiveSnapshot, WeatherSnapshot, AirQualityPoint } from "@sikhio/shared";
import type { DamReading } from "@sikhio/shared";

export interface BriefInput {
  executive: ExecutiveSnapshot | null;
  weather: WeatherSnapshot | null;
  airQuality: AirQualityPoint | null;
  openIncidents: number;
  dam: DamReading | null;
}

const ALERT_LEVEL_TH: Record<string, string> = {
  critical: "วิกฤต",
  warning: "เตือน",
  watch: "เฝ้าระวัง",
  info: "ข้อมูล",
};

const STATUS_TH: Record<string, string> = {
  "on-track": "ตามแผน",
  "at-risk": "มีความเสี่ยง",
  delayed: "ล่าช้า",
  completed: "เสร็จสิ้น",
};

const AQI_LABEL = (aqi: number): { th: string; en: string } => {
  if (aqi <= 50)  return { th: "ดี",      en: "Good" };
  if (aqi <= 100) return { th: "ปานกลาง", en: "Moderate" };
  if (aqi <= 150) return { th: "เริ่มมีผล", en: "Unhealthy for SG" };
  if (aqi <= 200) return { th: "มีผลกระทบ", en: "Unhealthy" };
  if (aqi <= 300) return { th: "อันตรายมาก", en: "Very Unhealthy" };
  return                  { th: "อันตรายร้ายแรง", en: "Hazardous" };
};

export function renderBriefMarkdown(input: BriefInput, now: Date = new Date()): string {
  const date = now.toLocaleDateString("th-TH", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
  const time = now.toLocaleTimeString("th-TH", {
    hour: "2-digit", minute: "2-digit", hour12: false,
  });

  const lines: string[] = [];
  lines.push(`# สรุปสถานการณ์เทศบาลเมืองสีคิ้ว · ${date} ${time}`);
  lines.push("");

  // Headline — Lam Takhong if we have it, otherwise reservoir average.
  if (input.dam) {
    lines.push(`**เขื่อนลำตะคอง** · Lam Takhong Dam: ${input.dam.pctNormal.toFixed(1)}% ของระดับปกติ (vs normal high)`);
    lines.push(`ที่เก็บน้ำ: ${input.dam.storageMcm.toFixed(1)} / ${input.dam.normalStorageMcm.toFixed(0)} MCM · น้ำใช้การได้: ${input.dam.usableMcm?.toFixed(1) ?? "—"} MCM`);
    lines.push("");
  }

  // Air + weather + incidents
  const bits: string[] = [];
  if (input.airQuality?.aqi != null) {
    const cat = AQI_LABEL(input.airQuality.aqi);
    bits.push(`คุณภาพอากาศ AQI **${input.airQuality.aqi}** (${cat.en})`);
  }
  if (input.weather?.tempC != null) {
    bits.push(`อุณหภูมิ ${input.weather.tempC.toFixed(0)}°C · ${input.weather.condition ?? "—"}`);
  }
  if (input.openIncidents > 0) {
    bits.push(`เหตุแจ้ง ${input.openIncidents} รายการ`);
  }
  if (bits.length) {
    lines.push("**สถานการณ์ทั่วไป**");
    lines.push(bits.map((b) => `- ${b}`).join("\n"));
    lines.push("");
  }

  // Alerts
  const alerts = input.executive?.alerts ?? [];
  if (alerts.length > 0) {
    lines.push("**การแจ้งเตือน · ALERTS**");
    for (const a of alerts) {
      const lvl = ALERT_LEVEL_TH[a.level] ?? a.level;
      lines.push(`- [${lvl}] **${a.title}** — ${a.message}`);
      if (a.actionRequired) lines.push(`  → ${a.actionRequired}`);
    }
    lines.push("");
  } else {
    lines.push("**สถานการณ์ปกติ** — ไม่มีการแจ้งเตือน");
    lines.push("");
  }

  // Initiatives — top 3 only (a Mayor forwarding a brief keeps it short)
  const initiatives = (input.executive?.initiatives ?? []).slice(0, 3);
  if (initiatives.length) {
    lines.push("**แผนงานที่กำลังดำเนินการ · INITIATIVES**");
    for (const i of initiatives) {
      const st = STATUS_TH[i.status] ?? i.status;
      lines.push(`- ${i.name} (${st} · ${i.progressPct}%) — ${i.owner}`);
    }
    lines.push("");
  }

  lines.push("—");
  lines.push("Sikhio Digital Twin · เทศบาลเมืองสีคิ้ว");
  lines.push(`https://sikhio.nonarkara.org`);

  return lines.join("\n");
}

/**
 * Copy text to the clipboard. Falls back to a hidden textarea on
 * browsers without the async Clipboard API. Returns true on success.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the textarea path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
